import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { valibotResolver } from "@hookform/resolvers/valibot";
import * as v from "valibot";
import { Button, Container, Form, Row, Col } from "react-bootstrap";
import styles from "./placeOrder.module.css";
import { useDispatch } from "react-redux";
import { productsApi } from "../../../features/api/products/productsApi";
import { OrderDetailsSection } from "./sections/OrderDetailsSection";
import { SampleDetailsSection } from "./sections/SampleDetailsSection";
import { PaymentDetailsSection } from "./sections/PaymentDetailsSection";
import { AsyncCustomerSelect } from "./sections/AsyncCustomerSelect";
import { Select } from "../../common/select/Select";
import { usePlaceOrderMutation } from "../../../features/api/order/orderApi";
import { CustomToast } from "../../common/customToast/CustomToast";
import { useIsSalesExecutive } from "../../../customHooks/roleChecker/useIsSalesExecutive";
import { useIsSeniorSalesExecutive } from "../../../customHooks/roleChecker/useIsSeniorSalesExecutive";
import { ReviewOrder } from "./reviewOrder/ReviewOrder";

let orderTypes = [];

const schema = v.object({
  orderType: v.union(
    [
      v.literal("INVOICE"),
      v.literal("DELIVERY"),
      v.literal("DRAFT"),
      v.literal("REFUND"),
      v.literal("REFUND_DELIVERY"),
    ],
    "Order type is required",
  ),
  customerId: v.number("Customer is required"),

  orderDetailsRequestDtos: v.optional(
    v.array(
      v.object({
        productId: v.number("Product is required"),
        quantity: v.number("Quantity is required"),
        vatCategory: v.union(
          [v.literal("A"), v.literal("B"), v.literal("C"), v.literal("D")],
          "Select a valid VAT category",
        ),
      }),
    ),
  ),
  sampleDetailsRequestDtos: v.optional(
    v.array(
      v.object({
        productId: v.number("Product is required"),
        quantity: v.number("Quantity is required"),
      }),
    ),
  ),
  paymentDetailsRequestDtos: v.optional(
    v.array(
      v.object({
        paymentMethod: v.union(
          [v.literal("CASH"), v.literal("BANK TRANSFER"), v.literal("CHECK")],
          "Select a valid payment method",
        ),
        amountPaid: v.number("Amount is required"),
      }),
    ),
  ),
});

export const PlaceOrder = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const isSalesExecutive = useIsSalesExecutive();
  const isSeniorSalesExecutive = useIsSeniorSalesExecutive();
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [productLabels, setProductLabels] = useState({});
  const [sampleProductLabels, setSampleProductLabels] = useState({});
  const [orderListWarning, setOrderListWarning] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [showReview, setShowReview] = useState(false);
  // review -
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [placeOrder, { isLoading, isSuccess, isError, error }] =
    usePlaceOrderMutation();
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: valibotResolver(schema),
    defaultValues: {
      orderType: "",
      customerId: "",
      orderDetailsRequestDtos: [],
      sampleDetailsRequestDtos: [],
      paymentDetailsRequestDtos: [],
    },
  });
  if (isSalesExecutive || isSeniorSalesExecutive) {
    orderTypes = ["DRAFT", "DELIVERY"];
  } else {
    orderTypes = ["REFUND", "REFUND_DELIVERY", "DRAFT", "DELIVERY"];
  }
  const orderType = watch("orderType");
  const orderFields = useFieldArray({
    control,
    name: "orderDetailsRequestDtos",
  });
  const sampleFields = useFieldArray({
    control,
    name: "sampleDetailsRequestDtos",
  });
  const paymentFields = useFieldArray({
    control,
    name: "paymentDetailsRequestDtos",
  });

  const loadProductOptions = async (search, loadedOptions, { page }) => {
    const response = await dispatch(
      productsApi.endpoints.getProducts.initiate({
        page: page - 1,
        size: 5,
        productName: search,
      }),
    );
    const data = response?.data;

    return {
      options:
        data?.content?.map((product) => ({
          value: product.id,
          label: `${product.name} | ${product.sellingPrice} €`,
          price: product.sellingPrice,
        })) || [],
      hasMore: !data?.last,
      additional: { page: page + 1 },
    };
  };
  const onSubmit = async (data) => {
    const hasOrderProducts = data.orderDetailsRequestDtos?.length > 0;
    const hasSampleProducts = data.sampleDetailsRequestDtos?.length > 0;

    if (!hasOrderProducts && !hasSampleProducts) {
      handleOrderListWarning();
      return;
    }

    // Open review modal with all form data
    const enrichedData = {
      ...data,
      customer: selectedCustomer,
    };

    setReviewData(enrichedData);
    setShowReview(true);
  };

  const handleFinalSubmit = async () => {
    if (!reviewData) return;

    const payload = {
      orderType: reviewData.orderType,
      customerId: reviewData.customerId,
      orderDetailsRequestDtos: reviewData.orderDetailsRequestDtos,
      ...(reviewData.sampleDetailsRequestDtos.length && {
        sampleDetailsRequestDtos: reviewData.sampleDetailsRequestDtos,
      }),
      ...(reviewData.paymentDetailsRequestDtos.length && {
        paymentDetailsRequestDtos: reviewData.paymentDetailsRequestDtos,
      }),
    };

    try {
      await placeOrder(payload).unwrap();

      setShowReview(false);
      handleSuccess();
    } catch (err) {
      console.log(err);
    }
  };

  const handleSuccess = () => {
    reset({
      orderType: "",
      customerId: "",
      orderDetailsRequestDtos: [],
      sampleDetailsRequestDtos: [],
      paymentDetailsRequestDtos: [],
    });
    setTimeout(() => {
      onSuccess();
    }, 3000);
  };
  const handleOrderListWarning = () => {
    setOrderListWarning(true);
    setTimeout(() => {
      setOrderListWarning(false);
    }, 3000);
  };

  return (
    <Container>
      <ReviewOrder
        show={showReview}
        orderData={reviewData}
        productLabels={productLabels}
        onConfirm={handleFinalSubmit}
        onCancel={() => setShowReview(false)}
        sampleProductLabels={sampleProductLabels}
      />
      {orderListWarning && (
        <CustomToast
          msg={`Please add product to proccess ${orderType}`}
          err={true}
        />
      )}
      {isSuccess && (
        <CustomToast msg={`${orderType} proccessed successfully`} />
      )}
      {isError && <CustomToast err={error} msg={error.data.errorMessage} />}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <h4>
            <span>Order </span>
            Info
          </h4>
          <Col sm={12} md={6}>
            <Select
              label="Order Type"
              field="orderType"
              defaultValue={"INVOICE"}
              register={register}
              options={orderTypes.map((t) => ({ value: t, label: t }))}
              errors={errors}
            />
          </Col>
          <Col sm={12} md={6}>
            <AsyncCustomerSelect
              control={control}
              errors={errors}
              setValue={setValue}
              selectedCustomerName={selectedCustomerName}
              setSelectedCustomerName={setSelectedCustomerName}
              setSelectedCustomer={setSelectedCustomer}
              dispatch={dispatch}
            />
          </Col>
        </Row>
        <hr />
        <OrderDetailsSection
          control={control}
          register={register}
          errors={errors}
          orderFields={orderFields}
          loadProductOptions={loadProductOptions}
          productLabels={productLabels}
          setProductLabels={setProductLabels}
        />
        <hr />
        <SampleDetailsSection
          control={control}
          register={register}
          errors={errors}
          sampleFields={sampleFields}
          loadProductOptions={loadProductOptions}
          sampleProductLabels={sampleProductLabels}
          setSampleProductLabels={setSampleProductLabels}
        />
        {orderType !== "DRAFT" && (
          <>
            <hr />
            <PaymentDetailsSection
              register={register}
              errors={errors}
              paymentFields={paymentFields}
            />
          </>
        )}

        <hr />
        <Row className="justify-content-center">
          <Col md={12} className="text-center">
            <Button type="submit" variant="success">
              Confirm
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};
